import { Character, CharacterVariant, NodeType, SerializedContent } from '@storyverse/shared'
import { characterRepository } from '../repository/CharacterRepository'

export class CharacterService {
    async getCharacters(projectId: string): Promise<Character[]> {
        return characterRepository.getByProjectId(projectId)
    }

    async getCharacter(id: string): Promise<Character | undefined> {
        return characterRepository.get(id)
    }

    async saveCharacter(character: Character): Promise<Character> {
        return characterRepository.saveCharacter(character)
    }

    async deleteCharacter(id: string): Promise<void> {
        await characterRepository.deleteCharacter(id)
    }

    /**
     * Create a default variant for a character.
     */
    createDefaultVariant(characterId: string): CharacterVariant {
        return {
            id: crypto.randomUUID(),
            character_id: characterId,
            label: 'Default',
            isDefault: true,
            created_at: new Date(),
            updated_at: new Date(),
        }
    }

    /**
     * Build a new Character object with a default variant already attached.
     */
    buildCharacter(fields: Omit<Character, 'id' | 'variants'>): Character {
        const id = crypto.randomUUID()
        return {
            ...fields,
            id,
            variants: [this.createDefaultVariant(id)],
            created_at: new Date(),
            updated_at: new Date(),
        }
    }

    /**
     * Simple client-side character extraction from screenplay content
     */
    extractCharactersFromScreenplay(content: SerializedContent[], projectId: string): Character[] {
        const characterNames = new Set<string>()

        content.forEach(node => {
            if (node.type === NodeType.Character) {
                const name = node.children?.[0]?.text?.trim().toUpperCase()
                if (name) characterNames.add(name)
            }
        })

        return Array.from(characterNames).map(fullname =>
            this.buildCharacter({ projectId, fullname })
        )
    }
}

export const characterService = new CharacterService()
